/* <bare-customer-reviews> custom element (Innovist reviews backend). Moved from sections/judgeme-reviews.liquid. */
(() => {
    const tagName = 'bare-customer-reviews';
    if (customElements.get(tagName)) return;

    class BareCustomerReviews extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.state = {
          page: 1,
          perPage: Number(this.dataset.perPage || 6),
          rating: '',
          verifiedOnly: false,
          withMediaOnly: false,
          sort: 'newest',
          loading: false,
          response: null,
          mediaImages: [],
          mediaLoaded: false,
        };
        this.cache = new Map();
        this.abortController = null;
        this.productId = this.dataset.productId;
        this.productHandle = this.dataset.productHandle;
        this.reviewsEndpoint = this.dataset.reviewsEndpoint;
        this.submissionEndpoint = this.dataset.submissionEndpoint;
        this.mediaLimit = 10;
        this.mediaPromise = null;
        this.mediaAbortController = null;
        this.showPinnedLabel = this.dataset.showPinnedLabel === 'true';
        this.reviewPhotoFiles = [];
        this.imagePreviewImages = [];
        this.imagePreviewIndex = -1;
        this.imageLoadToken = 0;
        this.dialogCloseTimers = new WeakMap();

        this.els = {
          average: this.querySelector('[data-average-rating]'),
          summaryStars: this.querySelector('[data-summary-stars]'),
          total: this.querySelector('[data-total-reviews]'),
          list: this.querySelector('[data-review-list]'),
          pagination: this.querySelector('[data-pagination]'),
          loading: this.querySelector('[data-loading-status]'),
          error: this.querySelector('[data-error]'),
          errorMessage: this.querySelector('[data-error-message]'),
          mediaSection: this.querySelector('[data-media-section]'),
          mediaStrip: this.querySelector('[data-media-strip]'),
          mediaDialog: this.querySelector('[data-media-dialog]'),
          mediaGallery: this.querySelector('[data-media-gallery]'),
          imageDialog: this.querySelector('[data-image-dialog]'),
          lightboxImage: this.querySelector('[data-lightbox-image]'),
          imagePrev: this.querySelector('[data-image-prev]'),
          imageNext: this.querySelector('[data-image-next]'),
          reviewDialog: this.querySelector('[data-review-dialog]'),
          reviewForm: this.querySelector('[data-review-form]'),
          formMessage: this.querySelector('[data-form-message]'),
          photoInput: this.querySelector('[data-review-photos]'),
          uploadField: this.querySelector('.bare-reviews__upload-field'),
          photoMessage: this.querySelector('[data-photo-message]'),
          selectedPhotos: this.querySelector('[data-selected-photos]'),
          reviewFormView: this.querySelector('[data-review-form-view]'),
          reviewSuccessView: this.querySelector('[data-review-success-view]'),
          ratingInputs: Array.from(this.querySelectorAll('[data-rating-input] input[name="rating"]')),
          ratingLabels: Array.from(this.querySelectorAll('[data-rating-value]')),
        };

        this.bindEvents();
        this.els.photoInput?.addEventListener('change', () => this.addReviewPhotos());
        this.els.ratingInputs.forEach((input) => {
          input.addEventListener('change', () => this.updateRatingStars());
        });
        this.updateRatingStars();

        // DOM building (cards, media strip, pagination) waits until the section
        // is within ~600px of the viewport; a fetch may start earlier.
        this.renderToken = 0;
        this.nearViewport = new Promise((resolve) => {
          if (!('IntersectionObserver' in window)) {
            resolve();
            return;
          }
          this.observer = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              this.observer.disconnect();
              resolve();
            },
            { rootMargin: '600px 0px' }
          );
          this.observer.observe(this);
        });

        // Try to paint straight from the judgeme.review_widget_data metafield
        // first — no network call. Any interaction beyond this default view
        // (pagination, filter, sort, "View all" photos, submitting a review)
        // still goes through the live API below, same as before.
        this.renderSkeletons(); // cheap placeholders keep the section's height until it is near

        if (this.hasInitialPayload()) {
          // JSON parse + card building run only once the section is near the viewport.
          this.nearViewport.then(() => this.hydrateFromMetafield() || this.startLiveLoad());
          return;
        }

        if (this.dataset.loadImmediately === 'true') {
          this.loadReviews(); // fetch now; renderResponse waits for nearViewport
        } else {
          this.nearViewport.then(() => this.loadReviews());
        }
      }

      startLiveLoad() {
        this.renderSkeletons();
        this.loadReviews();
      }

      yieldToMain() {
        if (window.scheduler && typeof window.scheduler.yield === 'function') return window.scheduler.yield();
        return new Promise((resolve) => setTimeout(resolve, 0));
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.mediaAbortController?.abort();
        this.observer?.disconnect();
      }

      bindEvents() {
        this.querySelector('[data-retry]')?.addEventListener('click', () => this.loadReviews({ force: true }));
        this.querySelector('[data-reset-filters]')?.addEventListener('click', () => {
          this.state.page = 1;
          this.state.rating = '';
          this.state.verifiedOnly = false;
          this.state.withMediaOnly = false;
          this.querySelector('[data-filter-rating]').value = '';
          this.querySelector('[data-filter-verified]').checked = false;
          this.querySelector('[data-filter-media]').checked = false;
          this.loadReviews();
        });

        this.querySelectorAll('[data-histogram-rating]').forEach((button) => {
          button.addEventListener('click', () => {
            this.state.rating = button.dataset.histogramRating;
            this.state.page = 1;
            this.querySelector('[data-filter-rating]').value = this.state.rating;
            this.loadReviews();
          });
        });

        this.querySelector('[data-toggle-filter]')?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.togglePopover('filter');
        });
        this.querySelector('[data-toggle-sort]')?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.togglePopover('sort');
        });
        this.querySelector('[data-apply-filter]')?.addEventListener('click', () => {
          this.state.rating = this.querySelector('[data-filter-rating]').value;
          this.state.verifiedOnly = this.querySelector('[data-filter-verified]').checked;
          this.state.withMediaOnly = this.querySelector('[data-filter-media]').checked;
          this.state.page = 1;
          this.closePopovers();
          this.loadReviews();
        });
        this.querySelector('[data-apply-sort]')?.addEventListener('click', () => {
          this.state.sort = this.querySelector('input[name^="bare-sort-"]:checked')?.value || 'newest';
          this.state.page = 1;
          this.closePopovers();
          this.loadReviews();
        });

        document.addEventListener('click', (event) => {
          if (!this.contains(event.target)) return;
          if (!event.target.closest('.bare-reviews__popover-wrap')) this.closePopovers();
        });
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.closePopovers();
        });

        this.querySelector('[data-view-all-media]')?.addEventListener('click', () => this.openAllMedia());
        this.querySelector('[data-close-media]')?.addEventListener('click', () =>
          this.closeDialog(this.els.mediaDialog)
        );
        this.querySelector('[data-close-image]')?.addEventListener('click', () => this.closeImageDialog());
        this.querySelector('[data-image-prev]')?.addEventListener('click', () => this.navigateImage(-1));
        this.querySelector('[data-image-next]')?.addEventListener('click', () => this.navigateImage(1));
        this.querySelector('[data-open-review-form]')?.addEventListener('click', () => {
          this.showReviewFormView();
          this.openDialog(this.els.reviewDialog);
        });
        this.els.reviewDialog?.addEventListener('click', (event) => {
          if (event.target !== this.els.reviewDialog) return;
          const rect = this.els.reviewDialog.getBoundingClientRect();
          const clickedOutside =
            event.clientX < rect.left ||
            event.clientX > rect.right ||
            event.clientY < rect.top ||
            event.clientY > rect.bottom;
          if (clickedOutside) this.closeDialog(this.els.reviewDialog);
        });
        this.querySelector('[data-close-review-form]')?.addEventListener('click', () =>
          this.closeDialog(this.els.reviewDialog)
        );
        this.querySelector('[data-review-success-back]')?.addEventListener('click', () => {
          this.closeDialog(this.els.reviewDialog, () => this.showReviewFormView());
        });
        [this.els.mediaDialog, this.els.imageDialog, this.els.reviewDialog].forEach((dialog) => {
          dialog?.addEventListener('cancel', (event) => {
            event.preventDefault();
            if (dialog === this.els.imageDialog) {
              this.closeImageDialog();
            } else {
              this.closeDialog(dialog);
            }
          });
        });
        this.els.reviewForm?.addEventListener('submit', (event) => this.submitReview(event));
      }

      openDialog(dialog) {
        if (!dialog) return;

        const closeTimer = this.dialogCloseTimers.get(dialog);
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          this.dialogCloseTimers.delete(dialog);
        }

        dialog.classList.remove('is-visible', 'is-closing');
        if (!dialog.open) dialog.showModal();

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (dialog.open) dialog.classList.add('is-visible');
          });
        });
      }

      closeDialog(dialog, afterClose = null) {
        if (!dialog?.open) {
          if (typeof afterClose === 'function') afterClose();
          return;
        }

        const closeTimer = this.dialogCloseTimers.get(dialog);
        if (closeTimer) window.clearTimeout(closeTimer);

        dialog.classList.remove('is-visible');
        dialog.classList.add('is-closing');

        const timer = window.setTimeout(() => {
          if (dialog.open) dialog.close();
          dialog.classList.remove('is-closing');
          this.dialogCloseTimers.delete(dialog);
          if (typeof afterClose === 'function') afterClose();
        }, 220);

        this.dialogCloseTimers.set(dialog, timer);
      }

      togglePopover(type) {
        const filter = this.querySelector('[data-filter-popover]');
        const sort = this.querySelector('[data-sort-popover]');
        const target = type === 'filter' ? filter : sort;
        const other = type === 'filter' ? sort : filter;
        if (other) other.hidden = true;
        if (target) target.hidden = !target.hidden;
        this.querySelector('[data-toggle-filter]')?.setAttribute('aria-expanded', String(filter && !filter.hidden));
        this.querySelector('[data-toggle-sort]')?.setAttribute('aria-expanded', String(sort && !sort.hidden));
      }

      closePopovers() {
        const filter = this.querySelector('[data-filter-popover]');
        const sort = this.querySelector('[data-sort-popover]');
        if (filter) filter.hidden = true;
        if (sort) sort.hidden = true;
        this.querySelector('[data-toggle-filter]')?.setAttribute('aria-expanded', 'false');
        this.querySelector('[data-toggle-sort]')?.setAttribute('aria-expanded', 'false');
      }

      // --- Metafield hydration (judgeme.review_widget_data) -----------------
      // Renders the first paint straight from the Judge.me metafield snapshot
      // embedded by Liquid, with zero network call. The metafield only ever
      // holds one cached page (whatever Judge.me's own default sort/page-size
      // last synced), so any interaction — page 2+, a filter, a different
      // sort, or "View all" photos — still goes through the live API exactly
      // as before. Returns true if hydration produced a renderable state.
      hydrateFromMetafield() {
        const raw = this.readInitialPayload();
        const payload = this.buildMetafieldPayload(raw);
        if (!payload) return false;

        this.state.page = 1;

        const galleryImages = this.collectImages(Array.isArray(raw.photo_gallery) ? raw.photo_gallery : []);

        this.renderResponse(payload, {
          media: {
            skipLiveFetch: true,
            presetImages: galleryImages,
            // photo_gallery is a curated preview, not the full media set, so
            // don't hide "View all" just because it happens to fit on-screen.
            forceShowViewAll: galleryImages.length > 0,
          },
        });

        return true;
      }

      hasInitialPayload() {
        const node = this.querySelector('script[data-initial-reviews]');
        return Boolean(node?.textContent?.trim());
      }

      readInitialPayload() {
        if (this.initialPayload !== undefined) return this.initialPayload;
        const node = this.querySelector('script[data-initial-reviews]');
        if (!node?.textContent) return (this.initialPayload = null);
        try {
          const parsed = JSON.parse(node.textContent);
          this.initialPayload = parsed && typeof parsed === 'object' ? parsed : null;
        } catch (error) {
          console.error('Could not parse the judgeme.review_widget_data snapshot:', error);
          this.initialPayload = null;
        }
        return this.initialPayload;
      }

      buildMetafieldPayload(raw) {
        if (!raw) return null;

        const numberOfReviews = Number(raw.number_of_reviews || 0);
        if (!numberOfReviews) return null;

        const reviews = (Array.isArray(raw.reviews) ? raw.reviews : [])
          .map((review) => this.normalizeMetafieldReview(review))
          .filter(Boolean);

        if (!reviews.length) return null;

        const distribution = {};
        (Array.isArray(raw.histogram) ? raw.histogram : []).forEach((entry) => {
          if (!entry || entry.rating == null) return;
          distribution[entry.rating] = { frequency: entry.frequency, percentage: entry.percentage };
        });

        // Normalize pagination against THIS section's configured page size
        // (data-per-page) rather than the metafield's own cached per_page, so
        // the page count stays consistent once live pagination takes over.
        const perPage = this.state.perPage || reviews.length;
        const totalPages = Math.max(1, Math.ceil(numberOfReviews / perPage));

        return {
          success: true,
          data: {
            summary: {
              averageRating: Number(raw.average_rating || 0),
              reviewCount: numberOfReviews,
              ratingDistribution: distribution,
              reviewsWithMediaCount: Array.isArray(raw.photo_gallery) ? raw.photo_gallery.length : 0,
            },
            reviews,
            pagination: {
              page: 1,
              totalPages,
            },
          },
        };
      }

      normalizeMetafieldReview(raw) {
        if (!raw) return null;
        return {
          reviewer: {
            displayName: raw.reviewer_name || 'Anonymous',
            verified: Boolean(raw.verified_buyer),
            verifiedStatus: 'Verified review',
          },
          pinned: Boolean(raw.pinned),
          createdAt: raw.created_at,
          rating: Number(raw.rating || 0),
          title: raw.title || '',
          body: raw.body || '',
          pictures_urls: Array.isArray(raw.pictures_urls) ? raw.pictures_urls : [],
        };
      }

      buildUrl(overrides = {}) {
        const endpoint = this.reviewsEndpoint;
        const state = { ...this.state, ...overrides };

        const page = Math.max(1, Number.parseInt(state.page, 10) || 1);
        const perPage = Math.min(20, Math.max(1, Number.parseInt(state.perPage, 10) || 6));

        const allowedSorts = new Set(['newest', 'oldest', 'most_helpful', 'pictures_first', 'videos_first']);

        const sort = allowedSorts.has(state.sort) ? state.sort : 'newest';

        const params = new URLSearchParams({
          productId: String(this.productId || '').trim(),
          page: String(page),
          perPage: String(perPage),
          sort,
        });

        if (state.rating) params.set('rating', String(state.rating));
        if (state.verifiedOnly) params.set('verifiedOnly', 'true');

        if (typeof state.media === 'string' && state.media.trim()) {
          params.set('media', state.media.trim());
        } else if (state.withMediaOnly) {
          params.set('media', 'all');
        }

        return `${endpoint}${endpoint.includes('?') ? '&' : '?'}${params.toString()}`;
      }

      async loadReviews({ force = false, page = null } = {}) {
        if (!this.reviewsEndpoint) {
          this.showError('The reviews backend URL is missing from this section file.');
          return false;
        }

        if (page !== null) {
          this.state.page = Math.max(1, Number.parseInt(page, 10) || 1);
        }

        const url = this.buildUrl();

        if (!force && this.cache.has(url)) {
          await this.nearViewport;
          this.setLoading(false);
          this.renderResponse(this.cache.get(url));
          return true;
        }

        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.setLoading(true);

        try {
          const response = await fetch(url, {
            method: 'GET',
            credentials: url.startsWith('/') ? 'same-origin' : 'omit',
            headers: {
              Accept: 'application/json',
            },
            signal: controller.signal,
          });

          const payload = await response.json().catch(() => null);

          if (!response.ok || !payload?.success) {
            const details = payload?.error?.details;
            const detailText = details ? ` ${JSON.stringify(details)}` : '';

            throw new Error(`${payload?.error?.message || `Review request failed (${response.status})`}${detailText}`);
          }

          if (this.abortController !== controller) return false;

          this.cache.set(url, payload);
          await this.nearViewport;
          if (this.abortController !== controller) return false;
          this.renderResponse(payload);
          return true;
        } catch (error) {
          if (error.name === 'AbortError' || this.abortController !== controller) {
            return false;
          }

          console.error('Review request failed:', {
            url,
            page: this.state.page,
            error,
          });

          this.showError(error.message || 'Reviews could not be loaded.');

          return false;
        } finally {
          if (this.abortController === controller) {
            this.abortController = null;
            this.setLoading(false);
          }
        }
      }

      renderResponse(payload, options = {}) {
        const data = payload.data || {};
        const summary = data.summary || {};
        const reviews = Array.isArray(data.reviews) ? data.reviews : [];
        this.state.response = payload;

        const average = Number(summary.averageRating || 0);
        this.els.average.textContent = average ? average.toFixed(1) : '0.0';
        this.els.summaryStars.style.setProperty('--rating', average);
        this.els.summaryStars.setAttribute('role', 'img');
        this.els.summaryStars.setAttribute('aria-label', `${average.toFixed(1)} out of 5 stars`);
        this.els.total.textContent = this.formatNumber(summary.reviewCount || 0);
        this.renderHistogram(summary.ratingDistribution || {}, summary.reviewCount || 0);
        this.renderReviews(reviews);
        this.renderPagination(data.pagination || {});
        this.renderMedia(reviews, summary.reviewsWithMediaCount || 0, options.media || {});
        this.els.error.hidden = true;
        this.els.loading.hidden = true;
        this.removeAttribute('aria-busy');
      }

      renderHistogram(distribution, total) {
        [1, 2, 3, 4, 5].forEach((rating) => {
          const entry = distribution[rating] ?? distribution[String(rating)] ?? 0;
          const count = Number(entry && typeof entry === 'object' ? entry.count ?? entry.frequency ?? 0 : entry);
          const suppliedPercentage = Number(entry && typeof entry === 'object' ? entry.percentage : NaN);
          const percentage = Number.isFinite(suppliedPercentage)
            ? Math.min(100, Math.max(0, suppliedPercentage))
            : total > 0
            ? Math.min(100, (count / total) * 100)
            : 0;
          const bar = this.querySelector(`[data-rating-bar="${rating}"]`);
          const countNode = this.querySelector(`[data-rating-count="${rating}"]`);
          if (bar) bar.style.width = `${percentage}%`;
          if (countNode) countNode.textContent = this.formatNumber(count);
        });
      }

      renderReviews(reviews) {
        const token = ++this.renderToken; // cancels any batched render still in flight
        this.els.list.replaceChildren();
        if (!reviews.length) {
          const empty = document.createElement('p');
          empty.textContent = 'No reviews match the selected filters.';
          empty.style.textAlign = 'center';
          empty.style.padding = '40px 0';
          this.els.list.append(empty);
          return;
        }
        // Build the cards in small batches, yielding to the main thread between
        // them so no single task gets long; a newer render cancels an older one.
        const CHUNK = 2;
        const run = async () => {
          for (let i = 0; i < reviews.length; i += CHUNK) {
            if (token !== this.renderToken) return;
            const fragment = document.createDocumentFragment();
            reviews.slice(i, i + CHUNK).forEach((review) => fragment.append(this.createReviewCard(review)));
            this.els.list.append(fragment);
            if (i + CHUNK < reviews.length) await this.yieldToMain();
          }
          if (token !== this.renderToken) return;
          requestAnimationFrame(() => {
            if (token === this.renderToken) this.initializeReadMoreButtons();
          });
        };
        run();
      }

      initializeReadMoreButtons() {
        // Writes first, then one batch of reads, then writes: avoids a forced
        // layout per card (layout thrash) when the list is (re)rendered.
        const pairs = [];
        this.els.list.querySelectorAll('[data-review-body]').forEach((body) => {
          const button = body.nextElementSibling;

          if (!button?.matches('[data-review-read-more]')) return;

          body.classList.remove('is-expanded');
          button.textContent = 'Read more';
          button.setAttribute('aria-expanded', 'false');
          pairs.push([body, button]);
        });

        const overflowing = pairs.map(([body]) => body.scrollHeight > body.clientHeight + 1);
        pairs.forEach(([, button], index) => {
          button.hidden = !overflowing[index];
        });
      }

      createReviewCard(review) {
        const card = document.createElement('article');
        card.className = 'bare-reviews__card';

        const head = document.createElement('div');
        head.className = 'bare-reviews__card-head';
        const reviewer = document.createElement('div');
        reviewer.className = 'bare-reviews__reviewer';
        const name = document.createElement('span');
        name.textContent = review.reviewer?.displayName || 'Anonymous';
        reviewer.append(name);
        if (review.reviewer?.verified) {
          const verified = document.createElement('span');
          verified.className = 'bare-reviews__verified';
          verified.textContent = '✓';
          verified.title = review.reviewer.verifiedStatus || 'Verified review';
          reviewer.append(verified);
        }
        if ((review.isPinned || review.pinned) && this.showPinnedLabel) {
          const pin = document.createElement('span');
          pin.className = 'bare-reviews__pin-label';
          pin.textContent = 'Pinned';
          reviewer.append(pin);
        }
        const date = document.createElement('time');
        date.className = 'bare-reviews__date';
        date.dateTime = review.createdAt || '';
        date.textContent = this.formatDate(review.createdAt);
        head.append(reviewer, date);

        const stars = document.createElement('div');
        stars.className = 'bare-reviews__stars bare-reviews__review-stars';
        stars.style.setProperty('--rating', Number(review.rating || 0));
        stars.setAttribute('role', 'img');
        stars.setAttribute('aria-label', `${review.rating || 0} out of 5 stars`);
        stars.innerHTML =
          '<span class="bare-reviews__stars-base" aria-hidden="true">★★★★★</span><span class="bare-reviews__stars-fill" aria-hidden="true">★★★★★</span>';

        const divider = document.createElement('div');
        divider.className = 'bare-reviews__divider';

        card.append(head, stars, divider);
        if (review.title) {
          const title = document.createElement('h3');
          title.className = 'bare-reviews__review-title';
          title.textContent = review.title;
          card.append(title);
        }
        const body = document.createElement('p');
        body.className = 'bare-reviews__review-body';
        body.dataset.reviewBody = '';
        body.textContent = review.body || '';

        const readMoreButton = document.createElement('button');
        readMoreButton.type = 'button';
        readMoreButton.className = 'bare-reviews__read-more';
        readMoreButton.dataset.reviewReadMore = '';
        readMoreButton.textContent = 'Read more';
        readMoreButton.hidden = true;
        readMoreButton.setAttribute('aria-expanded', 'false');

        readMoreButton.addEventListener('click', () => {
          const isExpanded = body.classList.toggle('is-expanded');
          readMoreButton.textContent = isExpanded ? 'Read less' : 'Read more';
          readMoreButton.setAttribute('aria-expanded', String(isExpanded));
        });

        card.append(body, readMoreButton);

        const images = this.getReviewImages(review);
        if (images.length) {
          const media = document.createElement('div');
          media.className = 'bare-reviews__card-media';
          images.slice(0, 4).forEach((image) => media.append(this.createImageButton(image)));
          card.append(media);
        }
        return card;
      }

      renderPagination(pagination) {
        const totalPages = Math.max(1, Number.parseInt(pagination.totalPages ?? pagination.total_pages ?? 1, 10) || 1);

        const currentPage = Math.min(
          totalPages,
          Math.max(
            1,
            Number.parseInt(
              pagination.page ?? pagination.currentPage ?? pagination.current_page ?? this.state.page ?? 1,
              10
            ) || 1
          )
        );

        this.state.page = currentPage;
        this.els.pagination.replaceChildren();

        if (totalPages <= 1) {
          this.els.pagination.hidden = true;
          return;
        }

        this.els.pagination.hidden = false;

        const paginationWrap = document.createElement('div');
        paginationWrap.className = 'pagination';

        const previousWrap = document.createElement('span');
        previousWrap.className = 'prev';
        previousWrap.append(
          this.nativePaginationButton({
            page: currentPage - 1,
            disabled: currentPage <= 1,
            direction: 'previous',
          })
        );

        const numbersWrap = document.createElement('span');
        numbersWrap.className = 'custom-number';

        this.visiblePages(currentPage, totalPages).forEach((item) => {
          const pageWrap = document.createElement('span');
          pageWrap.className = `page${item === currentPage ? ' current' : ''}`;

          if (item === 'ellipsis') {
            pageWrap.textContent = '…';
            pageWrap.setAttribute('aria-hidden', 'true');
          } else if (item === currentPage) {
            pageWrap.textContent = String(item);
            pageWrap.setAttribute('aria-current', 'page');
          } else {
            const pageButton = document.createElement('button');
            pageButton.type = 'button';
            pageButton.textContent = String(item);
            pageButton.dataset.reviewPage = String(item);
            pageButton.setAttribute('aria-label', `Page ${item}`);
            pageButton.addEventListener('click', () => this.goToReviewPage(item));
            pageWrap.append(pageButton);
          }

          numbersWrap.append(pageWrap);
        });

        const nextWrap = document.createElement('span');
        nextWrap.className = 'next';
        nextWrap.append(
          this.nativePaginationButton({
            page: currentPage + 1,
            disabled: currentPage >= totalPages,
            direction: 'next',
          })
        );

        paginationWrap.append(previousWrap, numbersWrap, nextWrap);
        this.els.pagination.append(paginationWrap);
      }

      visiblePages(current, total) {
        if (total <= 7) {
          return Array.from({ length: total }, (_, index) => index + 1);
        }

        const pages = [1];

        if (current > 4) pages.push('ellipsis');

        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);

        for (let page = start; page <= end; page += 1) {
          pages.push(page);
        }

        if (current < total - 3) pages.push('ellipsis');

        pages.push(total);
        return pages;
      }

      nativePaginationButton({ page, disabled, direction }) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `btn btn--large btn--custom btn--icon${disabled ? ' is-disabled' : ''}`;
        button.disabled = disabled;
        button.setAttribute('aria-label', direction === 'previous' ? 'Previous' : 'Next');

        if (disabled) {
          button.setAttribute('aria-disabled', 'true');
          button.tabIndex = -1;
        } else {
          button.dataset.reviewPage = String(page);
          button.addEventListener('click', () => this.goToReviewPage(page));
        }

        button.innerHTML =
          direction === 'previous'
            ? `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5 8 12l7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="icon__fallback-text visually-hidden">Previous</span>`
            : `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span class="icon__fallback-text visually-hidden">Next</span>`;

        return button;
      }

      async goToReviewPage(page) {
        const nextPage = Math.max(1, Number.parseInt(page, 10) || 1);

        const loaded = await this.loadReviews({
          page: nextPage,
          force: true,
        });

        if (loaded) {
          this.els.list.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }

      renderMedia(reviews, mediaCount, { skipLiveFetch = false, presetImages = null, forceShowViewAll = false } = {}) {
        if (!this.els.mediaSection || !this.els.mediaStrip) return;

        if (presetImages) {
          // Metafield-hydrated path: paint a cached snapshot of photos immediately,
          // with no network call. presetImages is typically a small curated subset
          // (e.g. judgeme's photo_gallery), so the "View all" button is shown
          // whenever it isn't empty rather than relying on the size heuristic below.
          this.renderMediaImages(presetImages, mediaCount, { forceShowViewAll });
        } else {
          // Render any images already present on the visible review page immediately.
          const pageImages = this.collectImages(reviews);
          if (!this.state.mediaImages.length && pageImages.length) {
            this.renderMediaImages(pageImages, mediaCount);
          }
        }

        if (skipLiveFetch) return;

        // Independently request reviews containing media so the strip can show
        // up to 10 unique customer images even when page 1 has fewer images.
        this.loadMediaPreview(mediaCount);
      }

      async loadMediaPreview(mediaCount = 0, { force = false } = {}) {
        if (!this.els.mediaSection || !this.els.mediaStrip) return [];

        if (!force && this.state.mediaLoaded) {
          this.renderMediaImages(this.state.mediaImages, mediaCount);
          return this.state.mediaImages;
        }

        if (!force && this.mediaPromise) return this.mediaPromise;

        this.mediaAbortController?.abort();
        const controller = new AbortController();
        this.mediaAbortController = controller;

        this.mediaPromise = (async () => {
          try {
            const url = this.buildUrl({
              page: 1,
              perPage: 20,
              sort: 'newest',
              rating: '',
              verifiedOnly: false,
              withMediaOnly: false,
              media: 'pictures',
            });

            const response = await fetch(url, {
              method: 'GET',
              credentials: url.startsWith('/') ? 'same-origin' : 'omit',
              headers: {
                Accept: 'application/json',
              },
              signal: controller.signal,
            });

            const payload = await response.json().catch(() => null);
            if (!response.ok || !payload?.success) {
              throw new Error(payload?.error?.message || `Media request failed (${response.status})`);
            }

            const mediaReviews = Array.isArray(payload.data?.reviews) ? payload.data.reviews : [];
            const images = this.collectImages(mediaReviews);

            this.state.mediaImages = images;
            this.state.mediaLoaded = true;
            this.renderMediaImages(images, mediaCount);
            return images;
          } catch (error) {
            if (error.name !== 'AbortError') {
              // Keep images gathered from the visible review page as fallback.
              this.renderMediaImages(this.state.mediaImages, mediaCount);
            }
            return this.state.mediaImages;
          } finally {
            if (this.mediaAbortController === controller) {
              this.mediaAbortController = null;
            }
            this.mediaPromise = null;
          }
        })();

        return this.mediaPromise;
      }

      renderMediaImages(images, mediaCount = 0, { forceShowViewAll = false } = {}) {
        const uniqueImages = Array.isArray(images) ? images : [];
        this.state.mediaImages = uniqueImages;
        this.els.mediaStrip.replaceChildren();

        uniqueImages.slice(0, this.mediaLimit).forEach((image) => {
          this.els.mediaStrip.append(this.createImageButton(image, 'bare-reviews__media-thumb'));
        });

        const shouldShow = uniqueImages.length > 0;
        this.els.mediaSection.hidden = !shouldShow;

        const viewAll = this.querySelector('[data-view-all-media]');
        if (viewAll) {
          const likelyHasMore =
            forceShowViewAll || uniqueImages.length > this.mediaLimit || Number(mediaCount || 0) > uniqueImages.length;
          viewAll.hidden = !shouldShow || !likelyHasMore;
        }
      }

      collectImages(reviews) {
        const map = new Map();
        reviews.forEach((review) => {
          this.getReviewImages(review).forEach((image) => {
            if (image.url && !map.has(image.url)) map.set(image.url, image);
          });
        });
        return [...map.values()];
      }

      getReviewImages(review) {
        const rawImages = Array.isArray(review.media?.images)
          ? review.media.images
          : Array.isArray(review.pictures)
          ? review.pictures
          : Array.isArray(review.picturesUrls)
          ? review.picturesUrls
          : Array.isArray(review.pictures_urls)
          ? review.pictures_urls
          : [];

        return rawImages
          .map((image) => {
            if (typeof image === 'string') {
              return {
                url: image,
                thumbnailUrl: image,
                alt: 'Customer review image',
              };
            }

            if (!image || typeof image !== 'object') return null;

            const url =
              image.url ||
              image.picture_url ||
              image.pictureUrl ||
              image.original_url ||
              image.originalUrl ||
              image.original ||
              image.huge ||
              image.src;

            if (!url) return null;

            return {
              ...image,
              url,
              thumbnailUrl:
                image.thumbnailUrl ||
                image.thumbnail_url ||
                image.thumbUrl ||
                image.thumb_url ||
                image.small ||
                image.compact ||
                url,
              alt: image.alt || 'Customer review image',
            };
          })
          .filter(Boolean);
      }

      createImageButton(image, className = '') {
        const button = document.createElement('button');
        button.type = 'button';
        if (className) button.className = className;
        const img = document.createElement('img');
        img.src = image.thumbnailUrl || image.url;
        img.alt = image.alt || 'Customer review image';
        img.loading = 'lazy';
        button.append(img);
        button.addEventListener('click', () => this.openImage(image.url, img.alt));
        return button;
      }

      async openAllMedia() {
        this.openDialog(this.els.mediaDialog);
        this.els.mediaGallery.replaceChildren();

        const images = this.state.mediaLoaded
          ? this.state.mediaImages
          : await this.loadMediaPreview(this.state.response?.data?.summary?.reviewsWithMediaCount || 0);

        images.forEach((image) => {
          this.els.mediaGallery.append(this.createImageButton(image));
        });
      }

      openImage(url, alt) {
        if (!this.els.imageDialog || !this.els.lightboxImage) return;

        const currentReviews = Array.isArray(this.state.response?.data?.reviews)
          ? this.state.response.data.reviews
          : [];
        const currentImages = this.collectImages(currentReviews);
        const imageMap = new Map();

        [...this.state.mediaImages, ...currentImages].forEach((image) => {
          if (image?.url && !imageMap.has(image.url)) imageMap.set(image.url, image);
        });

        if (!imageMap.has(url)) {
          imageMap.set(url, {
            url,
            thumbnailUrl: url,
            alt: alt || 'Customer review image',
          });
        }

        this.imagePreviewImages = [...imageMap.values()];
        this.imagePreviewIndex = this.imagePreviewImages.findIndex((image) => image.url === url);
        if (this.imagePreviewIndex < 0) this.imagePreviewIndex = 0;

        this.updateImageNavigation();
        if (!this.els.imageDialog.open) this.openDialog(this.els.imageDialog);
        this.renderPreviewImage();
      }

      renderPreviewImage() {
        if (!this.els.lightboxImage || !this.imagePreviewImages.length) return;

        const image = this.imagePreviewImages[this.imagePreviewIndex];
        if (!image?.url) return;

        const loadToken = ++this.imageLoadToken;
        const hasCurrentImage = Boolean(this.els.lightboxImage.getAttribute('src'));
        if (!hasCurrentImage) this.els.lightboxImage.style.visibility = 'hidden';

        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.onload = async () => {
          if (loadToken !== this.imageLoadToken) return;
          try {
            if (typeof preloader.decode === 'function') await preloader.decode();
          } catch (error) {}
          if (loadToken !== this.imageLoadToken) return;
          this.els.lightboxImage.src = image.url;
          this.els.lightboxImage.alt = image.alt || 'Customer review image';
          this.els.lightboxImage.style.visibility = 'visible';
        };
        preloader.onerror = () => {
          if (loadToken !== this.imageLoadToken) return;
          if (!hasCurrentImage) {
            this.els.lightboxImage.src = image.url;
            this.els.lightboxImage.alt = image.alt || 'Customer review image';
            this.els.lightboxImage.style.visibility = 'visible';
          }
        };
        preloader.src = image.url;
      }

      updateImageNavigation() {
        const hideNavigation = this.imagePreviewImages.length <= 1;
        if (this.els.imagePrev) this.els.imagePrev.hidden = hideNavigation;
        if (this.els.imageNext) this.els.imageNext.hidden = hideNavigation;
      }

      navigateImage(direction) {
        const total = this.imagePreviewImages.length;
        if (total <= 1) return;
        this.imagePreviewIndex = (this.imagePreviewIndex + direction + total) % total;
        this.renderPreviewImage();
      }

      closeImageDialog() {
        this.imageLoadToken += 1;
        this.closeDialog(this.els.imageDialog, () => {
          if (this.els.lightboxImage) {
            this.els.lightboxImage.removeAttribute('src');
            this.els.lightboxImage.style.visibility = 'hidden';
          }
        });
      }

      showReviewFormView() {
        if (this.els.reviewFormView) this.els.reviewFormView.hidden = false;
        if (this.els.reviewSuccessView) this.els.reviewSuccessView.hidden = true;
      }

      showReviewSuccessView() {
        if (this.els.reviewFormView) this.els.reviewFormView.hidden = true;
        if (this.els.reviewSuccessView) this.els.reviewSuccessView.hidden = false;
      }

      updateRatingStars() {
        const selected = Number(this.els.ratingInputs.find((input) => input.checked)?.value || 0);
        this.els.ratingLabels.forEach((label) => {
          const value = Number(label.dataset.ratingValue || 0);
          const active = value <= selected;
          label.textContent = active ? '★' : '☆';
          label.classList.toggle('is-selected', active);
          label.setAttribute('aria-label', `${value} star${value === 1 ? '' : 's'}`);
        });
      }

      addReviewPhotos() {
        const incomingFiles = Array.from(this.els.photoInput?.files || []);
        if (!incomingFiles.length) return;

        const combined = [...this.reviewPhotoFiles];
        incomingFiles.forEach((file) => {
          const duplicate = combined.some(
            (item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified
          );
          if (!duplicate && combined.length < 2) combined.push(file);
        });

        this.reviewPhotoFiles = combined;
        this.els.photoInput.value = '';
        this.validateReviewPhotos();
        this.renderSelectedPhotos();
      }

      renderSelectedPhotos() {
        if (!this.els.selectedPhotos) return;
        const files = [...this.reviewPhotoFiles];
        this.els.selectedPhotos.replaceChildren();
        this.els.selectedPhotos.hidden = files.length === 0;

        if (this.els.uploadField) {
          this.els.uploadField.hidden = files.length >= 2;
        }

        files.forEach((file, index) => {
          const chip = document.createElement('span');
          chip.className = 'bare-reviews__photo-chip';

          const name = document.createElement('span');
          name.textContent = file.name.replace(/\.[^.]+$/, '');

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.setAttribute('aria-label', `Remove ${file.name}`);
          remove.textContent = '×';
          remove.addEventListener('click', () => {
            this.reviewPhotoFiles.splice(index, 1);
            this.renderSelectedPhotos();
            this.validateReviewPhotos();
          });

          chip.append(name, remove);
          this.els.selectedPhotos.append(chip);
        });
      }

      validateReviewPhotos() {
        if (!this.els.photoInput) return true;

        const files = [...this.reviewPhotoFiles];
        const maxFiles = 2;
        const maxFileSize = 3 * 1024 * 1024;
        const allowedTypes = new Set(['image/jpeg', 'image/png']);
        let message = '';

        if (files.length > maxFiles) {
          message = 'You can upload a maximum of 2 images.';
        } else {
          const invalidType = files.find((file) => !allowedTypes.has(file.type));
          const oversizedFile = files.find((file) => file.size > maxFileSize);

          if (invalidType) {
            message = 'Only JPG and PNG images are allowed.';
          } else if (oversizedFile) {
            message = 'Each image must be 3 MB or smaller.';
          }
        }

        if (this.els.photoMessage) {
          this.els.photoMessage.hidden = !message;
          this.els.photoMessage.textContent = message;
        }

        return !message;
      }

      async submitReview(event) {
        event.preventDefault();
        if (!this.submissionEndpoint) {
          this.showFormMessage('Review submission endpoint is not configured.', true);
          return;
        }
        if (!this.els.reviewForm.reportValidity()) return;
        if (!this.validateReviewPhotos()) return;

        const sourceFormData = new FormData(this.els.reviewForm);
        const requestFormData = new FormData();
        requestFormData.set('productId', this.productId);
        requestFormData.set('name', String(sourceFormData.get('name') || '').trim());
        requestFormData.set('email', String(sourceFormData.get('email') || '').trim());
        requestFormData.set('rating', String(Number(sourceFormData.get('rating') || 5)));
        requestFormData.set('title', String(sourceFormData.get('title') || '').trim());
        requestFormData.set('body', String(sourceFormData.get('body') || '').trim());
        requestFormData.set('reviewerNameFormat', 'last_initial');
        requestFormData.set('consent', 'true');

        this.reviewPhotoFiles.forEach((file) => {
          requestFormData.append('photos', file, file.name);
        });

        const submit = this.querySelector('[data-submit-review]');
        submit.disabled = true;
        this.showFormMessage('Submitting your review…', false);
        try {
          const response = await fetch(this.submissionEndpoint, {
            method: 'POST',
            credentials: this.submissionEndpoint.startsWith('/') ? 'same-origin' : 'omit',
            headers: {
              Accept: 'application/json',
              'ngrok-skip-browser-warning': 'true',
              'Idempotency-Key': `${this.productId}-${Date.now()}-${
                crypto.randomUUID?.() || Math.random().toString(36).slice(2)
              }`,
            },
            body: requestFormData,
          });
          const result = await response.json().catch(() => null);
          if (!response.ok || !result?.success) throw new Error(result?.error?.message || 'Review submission failed.');
          this.els.reviewForm.reset();
          this.reviewPhotoFiles = [];
          this.updateRatingStars();
          this.renderSelectedPhotos();
          if (this.els.photoMessage) this.els.photoMessage.hidden = true;
          if (this.els.formMessage) this.els.formMessage.hidden = true;
          this.showReviewSuccessView();
        } catch (error) {
          this.showFormMessage(error.message || 'Review submission failed.', true);
        } finally {
          submit.disabled = false;
        }
      }

      showFormMessage(message, isError) {
        if (!this.els.formMessage) return;
        const normalizedMessage = String(message || '').trim();
        this.els.formMessage.hidden = !normalizedMessage;
        this.els.formMessage.textContent = normalizedMessage;
        this.els.formMessage.style.color = isError ? '#b42318' : '#176b36';
      }

      renderSkeletons() {
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < Math.min(this.state.perPage, 6); i += 1) {
          const card = document.createElement('div');
          card.className = 'bare-reviews__card bare-reviews__skeleton';
          card.setAttribute('aria-hidden', 'true');
          card.innerHTML =
            '<div class="bare-reviews__skeleton-line" style="width:48%;height:16px"></div><div class="bare-reviews__skeleton-line" style="width:30%"></div><div class="bare-reviews__skeleton-line" style="width:92%"></div><div class="bare-reviews__skeleton-line" style="width:82%"></div><div class="bare-reviews__skeleton-line" style="width:68%"></div>';
          fragment.append(card);
        }
        this.els.list.replaceChildren(fragment);
      }

      setLoading(loading) {
        this.state.loading = loading;
        if (loading) {
          this.setAttribute('aria-busy', 'true');
          this.els.error.hidden = true;
          if (!this.state.response) {
            this.renderSkeletons();
            this.els.loading.hidden = true;
          } else {
            this.els.loading.hidden = false;
          }
          this.els.list.style.opacity = this.state.response ? '.55' : '1';
          this.els.pagination.style.pointerEvents = 'none';
          this.els.pagination.style.opacity = this.state.response ? '.55' : '1';
          return;
        }

        this.removeAttribute('aria-busy');
        this.els.loading.hidden = true;
        this.els.list.style.opacity = '1';
        this.els.pagination.style.pointerEvents = '';
        this.els.pagination.style.opacity = '1';
      }

      showError(message) {
        this.setLoading(false);
        this.els.error.hidden = false;
        this.els.errorMessage.textContent = message;
        if (!this.state.response) {
          this.els.list.replaceChildren();
          this.els.pagination.hidden = true;
        }
      }

      formatNumber(value) {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN').format(Number(value || 0));
      }

      formatDate(value) {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return new Intl.DateTimeFormat(document.documentElement.lang || 'en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        }).format(date);
      }
    }

    customElements.define(tagName, BareCustomerReviews);
  })();
